<template>
  <DashboardLayout :pageTitle="loading ? 'Loading...' : `Patient: ${patient.email}`">
    <nav class="flex text-sm text-pharma-text-muted mb-6">
      <router-link to="/dashboard" class="hover:text-primary">Home</router-link>
      <span class="mx-2">/</span>
      <router-link to="/patients" class="hover:text-primary">Patients</router-link>
      <span class="mx-2">/</span>
      <span class="text-pharma-text-main font-bold">{{ loading ? '...' : patient.email }}</span>
    </nav>

    <div v-if="loading" class="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-3xl border border-gray-100">
      <span class="material-symbols-outlined animate-spin text-primary text-5xl">progress_activity</span>
      <p class="text-pharma-text-muted mt-4 font-bold tracking-tight">Loading patient records...</p>
    </div>

    <div v-else>
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
        <div class="flex flex-col md:flex-row justify-between items-center gap-6">
          <div class="flex items-center gap-6">
            <div class="h-11 w-11 rounded-full bg-blue-100 flex items-center justify-center text-primary font-bold">
              {{ patientInitials }}
            </div>
            
            <div class="space-y-1">
              <h2 class="text-2xl font-bold text-pharma-text-main">{{ patient.name || 'Unnamed Patient' }}</h2>
              <div class="flex items-center gap-2 text-pharma-text-muted text-sm">
                <span>Patient ID: <span class="font-mono font-bold">{{ patient.id }}</span></span>
                <span>•</span>
                <span class="flex items-center gap-1">
                  <span class="material-symbols-outlined text-[16px]">location_on</span>
                  {{ patient.city || 'N/A' }}
                </span>
                <span>•</span>
                <span>{{ patient.gender }}</span>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-6">
            <button @click="showDeleteModal = true" class="flex items-center gap-2 text-red-500 hover:text-red-700 font-bold text-sm transition-colors">
              <span class="material-symbols-outlined text-[18px]">delete</span>
              Delete Record
            </button>

            <div class="flex gap-3 border-l border-gray-100 pl-6">
              <button @click="openEditModal" class="flex items-center gap-2 px-4 py-2 bg-background-light border border-gray-100 rounded-xl font-bold text-sm text-pharma-text-main hover:bg-gray-200 transition">
                <span class="material-symbols-outlined text-[18px]">edit</span>
                Edit Profile
              </button>
              <button 
                @click="exportData" 
                :disabled="exporting"
                class="flex items-center gap-2 px-4 py-2 bg-primary rounded-xl font-bold text-sm text-white shadow-lg shadow-primary/20 hover:bg-[#2d4da3] transition disabled:opacity-70"
              >
                <span v-if="exporting" class="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>
                <span v-else class="material-symbols-outlined text-[18px]">file_download</span>
                {{ exporting ? 'Generating...' : 'Export Data' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6 overflow-hidden">
        <nav class="flex">
          <button 
            v-for="tab in ['Pain Entries', 'Medications', 'Doctors']" 
            :key="tab"
            @click="activeTab = tab"
            :class="[
              'px-8 py-4 text-sm font-bold transition-all relative',
              activeTab === tab ? 'text-primary' : 'text-pharma-text-muted hover:text-pharma-text-main'
            ]"
          >
            {{ tab }}
            <div v-if="activeTab === tab" class="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-t-full"></div>
          </button>
        </nav>
      </div>

      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 min-h-[400px]">
        <div v-if="activeTab === 'Pain Entries'">
          <h4 class="text-base font-bold text-pharma-text-main mb-6">Recent Pain Log History</h4>
          <div v-if="patient.painHistory.length" class="overflow-x-auto">
            <table class="w-full text-left">
              <thead>
                <tr class="text-pharma-text-muted text-sm border-b border-gray-50 bg-background-light/50">
                  <th class="px-4 py-4 font-semibold">Date & Time</th>
                  <th class="px-4 py-4 font-semibold">Pain Level</th>
                  <th class="px-4 py-4 font-semibold">Location</th>
                  <th class="px-4 py-4 font-semibold">Triggers</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-50">
                <tr v-for="entry in patient.painHistory" :key="entry.id" class="hover:bg-background-light transition">
                  <td class="px-4 py-4 text-sm text-pharma-text-main">
                    <p class="font-bold">{{ entry.date }}</p>
                    <p class="text-xs text-pharma-text-muted">{{ entry.time }}</p>
                  </td>
                  <td class="px-4 py-4">
                    <span :class="getPainLevelColor(entry.level)" class="px-2 py-1 rounded text-xs font-bold">
                      Level {{ entry.level }}/10
                    </span>
                  </td>
                  <td class="px-4 py-4 text-sm text-pharma-text-main">{{ entry.location }}</td>
                  <td class="px-4 py-4 text-xs text-pharma-text-muted italic">"{{ entry.trigger }}"</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-center py-12 text-sm text-pharma-text-muted italic font-medium">No records found.</p>
        </div>

        <div v-if="activeTab === 'Medications'">
          <h4 class="text-base font-bold text-pharma-text-main mb-6">Active Medications</h4>
          <div v-if="patient.meds.length" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="med in patient.meds" :key="med.name" class="p-4 rounded-xl border border-gray-100 bg-background-light/30 flex items-center justify-between transition-colors">
              <div class="flex items-center gap-4">
                <div class="size-10 rounded-full bg-blue-50 flex items-center justify-center text-primary">
                  <span class="material-symbols-outlined">pill</span>
                </div>
                <div>
                  <p class="text-sm font-bold text-pharma-text-main">{{ med.name }}</p>
                  <p class="text-xs text-pharma-text-muted">{{ med.dosage }} • {{ med.frequency }}</p>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="text-center py-12 text-sm text-pharma-text-muted italic font-medium">No medications listed.</p>
        </div>

        <div v-if="activeTab === 'Doctors'">
          <h4 class="text-base font-bold text-pharma-text-main mb-6">Assigned Medical Professionals</h4>
          <div v-if="patient.docs.length" class="overflow-x-auto">
            <table class="w-full text-left">
              <thead>
                <tr class="text-pharma-text-muted text-sm border-b border-gray-50 bg-background-light/50">
                  <th class="px-4 py-4 font-semibold">Doctor Name</th>
                  <th class="px-4 py-4 font-semibold">Specialty</th>
                  <th class="px-4 py-4 font-semibold">Contact Info</th>
                  <th class="px-4 py-4 font-semibold text-right">Date Linked</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-50">
                <tr v-for="doc in patient.docs" :key="doc.name" class="hover:bg-background-light transition">
                  <td class="px-4 py-4">
                    <p class="text-sm font-bold text-pharma-text-main">{{ doc.name }}</p>
                    <p class="text-xs text-pharma-text-muted">Specialist</p>
                  </td>
                  <td class="px-4 py-4">
                    <span class="px-2 py-1 rounded text-xs font-bold uppercase bg-blue-50 text-primary">{{ doc.spec }}</span>
                  </td>
                  <td class="px-4 py-4">
                    <p class="text-sm text-pharma-text-main">{{ doc.email }}</p>
                    <p class="text-xs text-pharma-text-muted">{{ doc.phone }}</p>
                  </td>
                  <td class="px-4 py-4 text-sm text-pharma-text-main text-right">{{ doc.lastDate }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-center py-12 text-sm text-pharma-text-muted italic font-medium">No doctors assigned.</p>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showEditModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" @click="showEditModal = false"></div>
        <div class="relative bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in duration-300">
          <div class="p-8">
            <h3 class="text-2xl font-black text-pharma-text-main text-center mb-6 tracking-tight">Update Profile</h3>
            
            <div v-if="editSuccessLabel" class="mb-6 p-4 bg-green-50 border border-green-100 rounded-xl flex items-center gap-3">
              <span class="material-symbols-outlined text-green-500">check_circle</span>
              <p class="text-green-700 text-sm font-bold">{{ editSuccessLabel }}</p>
            </div>

            <div v-if="editErrorLabel" class="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3">
              <span class="material-symbols-outlined text-red-500">error</span>
              <p class="text-red-700 text-sm font-bold">{{ editErrorLabel }}</p>
            </div>

            <div class="space-y-5">
              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Email</label>
                <input v-model="editForm.email" type="email" class="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none bg-[#f8f9fb]" />
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">New Password (Optional)</label>
                <input v-model="editForm.password" type="password" class="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none bg-[#f8f9fb]" />
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Confirm New Password</label>
                <input v-model="editForm.password_confirmation" type="password" class="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none bg-[#f8f9fb]" />
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">City</label>
                <input v-model="editForm.city" type="text" class="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none bg-[#f8f9fb]" />
              </div>
              <div class="pt-4 flex gap-3">
                <button @click="showEditModal = false" class="flex-1 h-12 bg-gray-100 font-bold rounded-xl hover:bg-gray-200 transition">Cancel</button>
                <button @click="submitUpdate" :disabled="editSubmitting" class="flex-1 h-12 bg-primary text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2">
                  <span v-if="editSubmitting" class="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
                  <span>{{ editSubmitting ? 'Updating...' : 'Update Profile' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showDeleteModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-pharma-text-main/40 backdrop-blur-sm" @click="showDeleteModal = false"></div>
        <div class="relative bg-white rounded-3xl w-full max-w-md shadow-2xl p-8 animate-in zoom-in duration-200">
          <div class="flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-6 text-red-600 mx-auto">
            <span class="material-symbols-outlined text-3xl">warning</span>
          </div>
          <h3 class="text-xl font-bold text-pharma-text-main mb-2 text-center">Delete Patient Record?</h3>
          <p class="text-sm text-pharma-text-muted mb-8 text-center">Are you sure you want to delete <span class="font-bold text-pharma-text-main">{{ patient.name }}</span>? This cannot be undone.</p>
          <div class="flex gap-4">
            <button @click="showDeleteModal = false" class="flex-1 py-3 bg-background-light font-bold rounded-xl hover:bg-gray-200">Cancel</button>
            <button @click="handleDelete" class="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl shadow-lg hover:bg-red-700">Delete</button>
          </div>
        </div>
      </div>
    </Teleport>
  </DashboardLayout>
</template>

<script>
import DashboardLayout from './layouts/DashboardLayout.vue'
import api from '@/services/api'

export default {
  name: 'PatientProfile',
  components: { DashboardLayout },
  data() {
    return {
      loading: true,
      exporting: false,
      activeTab: 'Pain Entries',
      showDeleteModal: false,
      showEditModal: false,
      editSubmitting: false,
      editErrorLabel: '',
      editSuccessLabel: '',
      editForm: { email: '', password: '', password_confirmation: '', city: '' },
      patient: { 
        name: "", 
        id: "", 
        email: "", 
        gender: "", 
        city: "", 
        painHistory: [], 
        meds: [], 
        docs: [] 
      }
    }
  },
  computed: {
    patientInitials() {
      if (!this.patient.name) return '?';
      const names = this.patient.name.split(' ');
      return names.length >= 2 
        ? `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase() 
        : names[0].charAt(0).toUpperCase();
    }
  },
  methods: {
    async fetchPatientData() {
      this.loading = true;
      try {
        const response = await api.get(`/patients/${this.$route.params.id}`);
        this.patient = response.data;
      } catch (error) {
        if (error.response?.status === 404) this.$router.push({ name: 'Patients' });
      } finally {
        this.loading = false;
      }
    },
    async exportData() {
      this.exporting = true;
      try {
        const response = await api.get(`/patients/${this.$route.params.id}/export`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Patient_Report_${this.patient.id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        alert("Export failed.");
      } finally {
        this.exporting = false;
      }
    },
    openEditModal() {
      this.editForm = { 
        email: this.patient.email, 
        city: this.patient.city || '', 
        password: '', 
        password_confirmation: '' 
      };
      this.editErrorLabel = '';
      this.editSuccessLabel = '';
      this.showEditModal = true;
    },
    async submitUpdate() {
      this.editErrorLabel = '';
      this.editSuccessLabel = '';
      if (this.editForm.password && this.editForm.password !== this.editForm.password_confirmation) {
        this.editErrorLabel = "Passwords do not match.";
        return;
      }
      this.editSubmitting = true;
      try {
        const response = await api.put(`/patients/${this.$route.params.id}`, this.editForm);
        this.editSuccessLabel = response.data.message || "Profile updated!";
        setTimeout(() => {
          this.fetchPatientData();
          this.showEditModal = false;
        }, 1500);
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.editErrorLabel = errors ? Object.values(errors)[0][0] : "Update failed.";
      } finally {
        this.editSubmitting = false;
      }
    },
    async handleDelete() {
      try {
        await api.delete(`/patients/${this.$route.params.id}`);
        this.$router.push({ name: 'Patients' });
      } catch (error) {
        alert("Delete failed.");
      }
    },
    getPainLevelColor(level) {
      if (level >= 7) return 'bg-red-50 text-red-600';
      if (level >= 4) return 'bg-orange-50 text-orange-600';
      return 'bg-green-50 text-green-600';
    }
  },
  mounted() {
    this.fetchPatientData();
  }
}
</script>